"use client";

import { Box, Link } from "@mui/material";
import { useCallback, useEffect, useRef } from "react";
import StyledDivider from "@/src/components/common/styled-divider/styled-divider";
import type ArticleMetricsProps from "@/src/_types/Article/article-metrics";
import { BadgeElement } from "@/src/_types/Article/article-metrics";
import NextLink from "next/link";

const PUBLONS_BADGE_PROXY_URL = "/api/publons-badge";
const PLUMX_WIDGET_URL = "https://plu.mx/plum/a";
const POPUP_SELECTOR = ".publons-badge-popup";
const ICON_SELECTOR = ".publons-badge-icon";

const ArticleMetrics: React.FC<ArticleMetricsProps> = ({
  publonBadge,
  doi,
  hasPlumx,
}) => {
  const hidePopupElements = useCallback((element: HTMLElement) => {
    const arrow = element.parentElement?.querySelector(".arrow") as HTMLElement;
    if (arrow) arrow.style.display = "none";
    element.style.display = "none";
  }, []);

  const setupPopupPosition = useCallback(
    (element: HTMLElement, popup: HTMLElement) => {
      const rect = element.getBoundingClientRect();
      const overflow = rect.left + 250 + 60 - window.innerWidth;
      popup.style.left = overflow > 0 ? `-${overflow}px` : "0";
      popup.style.maxWidth = `${window.innerWidth - rect.left - 40}px`;
      popup.style.display = "block";

      const arrow = element.querySelector(".arrow") as HTMLElement;
      if (arrow) arrow.style.display = "block";
    },
    [],
  );

  const handleBadgeClick = useCallback(
    (url: string | null, isTouched: boolean) => {
      if (!url) return;
      if (isTouched) {
        window.open(url, "_blank");
      } else {
        window.open(url, "_blank");
      }
    },
    [],
  );

  const setupBadgeEventListeners = useCallback(
    (element: HTMLElement, popup: HTMLElement, icon: HTMLElement) => {
      let isTouched = false;

      element.addEventListener("mouseenter", () => {
        document
          .querySelectorAll(POPUP_SELECTOR)
          .forEach((el) => hidePopupElements(el as HTMLElement));
        setupPopupPosition(element, popup);
      });

      element.addEventListener("mouseleave", () => {
        hidePopupElements(popup);
      });

      icon.addEventListener("touchstart", () => {
        isTouched = true;
      });

      icon.addEventListener("mousedown", () => {
        handleBadgeClick(icon.getAttribute("url"), isTouched);
        isTouched = false;
      });
    },
    [hidePopupElements, setupPopupPosition, handleBadgeClick],
  );

  const initializePublonsBadge = useCallback(async () => {
    if (!doi) return;

    const badges = document.querySelectorAll(".publons-badge");
    badges.forEach(async (badge) => {
      const element = badge as BadgeElement;
      const highConfidence = element.high_confidence === "true";

      try {
        const response = await fetch(
          `${PUBLONS_BADGE_PROXY_URL}?doi=${encodeURIComponent(doi)}&high_confidence=${highConfidence}`,
        );

        if (!response.ok) return;

        const { html } = await response.json();
        if (!html) return;

        element.innerHTML = html;

        const popup = element.querySelector(POPUP_SELECTOR) as HTMLElement;
        const icon = element.querySelector(ICON_SELECTOR) as HTMLElement;

        if (popup && icon) {
          setupBadgeEventListeners(element, popup, icon);
        }
      } catch (error) {
        console.error("Error initializing Publons badge:", error);
      }
    });
  }, [doi, setupBadgeEventListeners]);

  useEffect(() => {
    if (publonBadge) {
      initializePublonsBadge();
    }
  }, [publonBadge, initializePublonsBadge]);

  const rootRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!hasPlumx || !rootRef.current) return;

    let script: HTMLScriptElement | null = null;
    const observer = new IntersectionObserver(
      (entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;
        observer.disconnect();
        script = document.createElement("script");
        script.src = "//cdn.plu.mx/widget-popup.js";
        script.async = true;
        document.head.appendChild(script);
      },
      { rootMargin: "200px" },
    );
    observer.observe(rootRef.current);

    return () => {
      observer.disconnect();
      script?.remove();
    };
  }, [hasPlumx]);

  return (
    <Box ref={rootRef}>
      <StyledDivider label="Metrics" />
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
        }}
      >
        {publonBadge && (
          <Box
            className="publons-badge"
            id="publons-badge-id"
            sx={{
              maxWidth: 40,
            }}
          />
        )}
        {hasPlumx && (
          <Box>
            <Link
              component={NextLink}
              href={`${PLUMX_WIDGET_URL}/?doi=${doi}`}
              aria-label="PlumX Metrics"
              data-popup="bottom"
              data-size="medium"
              className="plumx-plum-print-popup plum-liberty-theme"
              data-site="plum"
              data-hide-when-empty="true"
            />
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default ArticleMetrics;
